import { MdOutlineKeyboardArrowLeft } from 'react-icons/md';
import { PiDotOutlineFill } from 'react-icons/pi';
import { NavLink } from 'react-router-dom';
import { Accordion, AccordionItem } from '@ds';
import { ConditionalRender } from '@ui';
import { NavLinkRenderProps } from '@/types';
import { AdminMenuType } from '../config';

type AdminMenuProps = {
  menu: AdminMenuType;
};

const DEFAULT_CLASSES = 'w-full h-9 flex items-center rounded-lg';
const ACTIVE_CLASSES = 'text-base-90 bg-black';
const INACTIVE_CLASSES = 'text-base-10 bg-transparent hover:text-base-90 hover:bg-black';

const getChildLinkStyles = ({ isActive }: NavLinkRenderProps): string => {
  const defaultStyles = `${DEFAULT_CLASSES} text-m-m pl-4 pr-2`;
  if (isActive) {
    return `${defaultStyles} ${ACTIVE_CLASSES}`;
  }

  return `${defaultStyles} ${INACTIVE_CLASSES}`;
};

const getLinkStyles = ({ isActive }: NavLinkRenderProps): string => {
  const defaultStyles = `${DEFAULT_CLASSES} gap-3 text-l-m px-2`;
  if (isActive) {
    return `${defaultStyles} ${ACTIVE_CLASSES} [&>svg]:text-base-90`;
  }

  return `${defaultStyles} ${INACTIVE_CLASSES} [&>svg]:text-base-20 [&>svg]:hover:text-base-90`;
};

export const AdminMenu = ({ menu }: AdminMenuProps) => {
  return (
    <div className="w-[272px] py-6 px-2 bg-base-90 flex flex-col gap-3 shrink-0 overflow-auto">
      {menu.map((item) => {
        const isSection = item.type === 'section';
        const { hasPermission } = item;

        if (isSection && hasPermission) {
          return (
            <Accordion defaultSelectedKeys="all" key={item.link} isCompact>
              <AccordionItem
                key={item.link}
                indicator={<MdOutlineKeyboardArrowLeft size={24} />}
                startContent={item.icon}
                aria-label={item.label}
                title={item.label}
                as="div"
                classNames={{
                  trigger: `group px-2 rounded-lg hover:text-base-90 hover:bg-black`,
                  startContent: 'text-base-20 group-hover:text-base-90',
                  title: 'text-l-m! text-base-5 group-hover:text-base-90',
                  indicator: 'text-neutral-black',
                  content: 'flex flex-col gap-1',
                }}
              >
                {item.children.map((child) => (
                  <ConditionalRender key={`${item.link}${child.to}`} show={child.hasPermission}>
                    <NavLink to={`${item.link}${child.to}`} className={getChildLinkStyles}>
                      <PiDotOutlineFill size={23} />
                      <span>{child.label}</span>
                    </NavLink>
                  </ConditionalRender>
                ))}
              </AccordionItem>
            </Accordion>
          );
        }

        return (
          <ConditionalRender key={item.link} show={hasPermission}>
            <div className="px-2 w-full">
              <NavLink to={item.link} className={getLinkStyles}>
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            </div>
          </ConditionalRender>
        );
      })}
    </div>
  );
};
